import { Box, Button } from "@mui/material";

interface CustomPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  groupSize?: number;
}

export const CustomPagination = ({ page, totalPages, onPageChange, groupSize = 5 }: CustomPaginationProps) => {
  if (totalPages <= 1) return null;

  const pageGroupStart = Math.floor((page - 1) / groupSize) * groupSize + 1;
  const pageGroupEnd = Math.min(pageGroupStart + groupSize - 1, totalPages);
  const visiblePages = Array.from({ length: pageGroupEnd - pageGroupStart + 1 }, (_, i) => pageGroupStart + i);

  const baseButtonSx = {
    minWidth: 0,
    px: 1.5,
    py: 0.75,
    fontSize: 14,
    fontWeight: 400,
    borderRadius: 1,
    border: "1px solid",
    borderColor: "divider",
    color: "text.primary",
    "&:hover": { bgcolor: "grey.50" },
  };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: "2px", mt: "6px" }}>
      {/* 이전 */}
      <Button
        variant="text"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        sx={{ ...baseButtonSx, "&.Mui-disabled": { opacity: 0.4 } }}
      >
        이전
      </Button>

      {/* 그룹 이전 (...) */}
      {pageGroupStart > 1 && (
        <Button variant="text" onClick={() => onPageChange(pageGroupStart - 1)} sx={baseButtonSx}>
          ...
        </Button>
      )}

      {/* 페이지 번호 */}
      {visiblePages.map((p) => (
        <Button
          key={p}
          variant="text"
          onClick={() => onPageChange(p)}
          sx={{
            ...baseButtonSx,
            ...(page === p && {
              bgcolor: "#3b82f6", // bg-blue-500
              borderColor: "#3b82f6",
              color: "white",
              "&:hover": { bgcolor: "#2563eb" }, // hover:bg-blue-600
            }),
          }}
        >
          {p}
        </Button>
      ))}

      {/* 그룹 다음 (...) */}
      {pageGroupEnd < totalPages && (
        <Button variant="text" onClick={() => onPageChange(pageGroupEnd + 1)} sx={baseButtonSx}>
          ...
        </Button>
      )}

      {/* 다음 */}
      <Button
        variant="text"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        sx={{ ...baseButtonSx, "&.Mui-disabled": { opacity: 0.4 } }}
      >
        다음
      </Button>
    </Box>
  );
};
